'use client';

import dayjs from 'dayjs';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { zhCN } from 'react-day-picker/locale';

import { useControl } from 'src/hooks';
import { ymd } from 'src/lib/lang/time';
import { cn } from 'src/lib/utils';

import { Input } from '../input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { YearPicker } from '../ui/year-picker';

const DateRegex = /^\d{4}-\d{2}-\d{2}$/;
const YearRegex = /^\d{4}$/;

interface DatePickerProps {
  defaultValue?: Date;
  value?: Date;
  onChange?: (value: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  captionLayout?: 'dropdown' | 'dropdown-years' | 'label' | 'dropdown-months' | undefined;
  // 用来控制禁止选择日期
  disabled?: boolean | ((date: Date) => boolean) | Date[];
  /** 选择器模式：date 为日期选择（默认），year 为年份选择 */
  mode?: 'date' | 'year';
  /** 年份模式下的最小年份 */
  fromYear?: number;
  /** 年份模式下的最大年份 */
  toYear?: number;
}

export function DatePicker({
  defaultValue,
  value,
  onChange,
  placeholder,
  className,
  clearable,
  captionLayout = 'dropdown-years',
  disabled,
  mode = 'date',
  fromYear,
  toYear,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = useControl(defaultValue, value, onChange);

  // 格式化函数：根据 mode 返回不同格式
  const formatDate = React.useCallback(
    (date: Date | undefined): string => {
      if (!date) return '';
      if (mode === 'year') {
        return date.getFullYear().toString();
      }
      return ymd(date) || '';
    },
    [mode]
  );

  const [inputValue, setInputValue] = React.useState(formatDate(date));

  const handleSelect = (date: Date | undefined) => {
    // 如果是清除操作，且允许清除，则可以清空日期为 undefined
    if (!clearable || date) {
      setDate(date);
    }
    setOpen(false);
  };

  const isDateDisabled = React.useCallback(
    (date: Date) => {
      if (!disabled) return false;

      if (typeof disabled === 'function') {
        return disabled(date);
      }

      if (Array.isArray(disabled)) {
        const timestamp = date.valueOf();
        const disabledSet = new Set(disabled.map((d) => dayjs(d).valueOf()));
        return disabledSet.has(timestamp);
      }

      return false;
    },
    [disabled]
  );

  const handleInputChange = (val: string) => {
    setInputValue(val);
    if (val === '' || val === undefined) {
      setDate(undefined);
      return;
    }

    // 年份模式
    if (mode === 'year') {
      if (!YearRegex.test(val)) return;
      const year = parseInt(val, 10);
      const date = new Date(year, 0, 1);
      if (isNaN(date.getTime())) return;
      if (isDateDisabled(date)) return;
      setDate(date);
      return;
    }

    // 日期模式
    if (!DateRegex.test(val)) return;

    const date = new Date(val);
    if (isNaN(date.getTime())) return;
    if (isDateDisabled(date)) return;

    // 如果日期有效则更新到 innerDate
    setDate(date);
  };

  const handleInputBlur = () => {
    // 年份模式
    if (mode === 'year') {
      if (!YearRegex.test(inputValue)) {
        setInputValue(formatDate(date));
        return;
      }
      const year = parseInt(inputValue, 10);
      const newDate = new Date(year, 0, 1);
      if (isNaN(newDate.getTime()) || isDateDisabled(newDate)) {
        setInputValue(formatDate(date));
      }
      return;
    }

    // 日期模式
    const newDate = new Date(inputValue);
    // 无效日期，重置为之前的值
    if (!DateRegex.test(inputValue) || isNaN(newDate.getTime()) || isDateDisabled(newDate)) {
      setInputValue(formatDate(date));
    }
  };

  React.useEffect(() => {
    // date 变化时同步到输入框
    setInputValue(formatDate(date));
  }, [date, formatDate]);

  const btn = (
    <span
      id="date-picker"
      role="button"
      tabIndex={-1}
      className="h-6 w-6 -mr-1 text-muted-foreground hover:text-accent-foreground flex items-center justify-center rounded-md transition hover:shadow-xs hover:bg-accent select-none outline-none"
      aria-label="Select date"
    >
      <CalendarIcon className="size-4" />
      <span className="sr-only">Select date</span>
    </span>
  );

  const shouldClear = clearable && !!inputValue; // 输入框有值时显示清除按钮

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Input
          value={inputValue}
          placeholder={placeholder || (mode === 'year' ? '请选择年份' : '请选择日期')}
          suffix={shouldClear ? undefined : btn}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
          onValueChange={handleInputChange}
          className={cn('pr-9', className)}
          onBlur={handleInputBlur}
          clearable={shouldClear}
          disabled={disabled === true}
        />
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto overflow-hidden p-0"
        align="end"
        alignOffset={-8}
        sideOffset={10}
      >
        {mode === 'year' ? (
          <YearPicker
            selected={date}
            onSelect={handleSelect}
            disabled={disabled}
            fromYear={fromYear}
            toYear={toYear}
          />
        ) : (
          <Calendar
            mode="single"
            selected={date}
            captionLayout={captionLayout}
            onSelect={handleSelect}
            locale={zhCN}
            disabled={disabled}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
